import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./game.css";
import Swal from "sweetalert2";
import { updateScore, getScore, updategameover } from "../../scoreservice";
import { fireCornerConfetti } from "./confetti";

const ROWS = 8;
const COLS = 8;
const MIN_MATCH = 3;

function getCandyTypes(themeData, uploadsBaseUrl) {
  if (!themeData) return [];
  const types = [];
  const base = (uploadsBaseUrl || "").trim();
  const baseWithSlash = base && !base.endsWith("/") ? base + "/" : base;
  const add = (path) => {
    if (!path || typeof path !== "string") return;
    const trimmed = path.trim();
    if (!trimmed) return;
    if (trimmed.startsWith("http")) types.push(trimmed);
    else types.push(baseWithSlash + trimmed);
  };
  add(themeData.candy_icon1);
  add(themeData.candy_icon2);
  add(themeData.candy_icon3);
  add(themeData.candy_icon4);
  add(themeData.candy_icon5);
  if (types.length === 0) {
    add(themeData.puzzle_image);
    add(themeData.cloud_image);
    add(themeData.dreamcatchter_image);
    add(themeData.all_icon_image);
    if (Array.isArray(themeData.default_cat)) {
      themeData.default_cat.forEach((item) => {
        if (typeof item === "string" && (item.startsWith("http") || item.includes("/") || item.endsWith(".png") || item.endsWith(".jpg"))) add(item);
      });
    }
    if (Array.isArray(themeData.negative_cat)) {
      themeData.negative_cat.forEach((item) => {
        if (typeof item === "string" && (item.startsWith("http") || item.includes("/") || item.endsWith(".png") || item.endsWith(".jpg"))) add(item);
      });
    }
  }
  if (types.length === 0) add(themeData.all_icon_image);
  const filtered = types.filter(Boolean);
  while (filtered.length < 2 && filtered[0]) filtered.push(filtered[0]);
  return filtered;
}

function createEmptyGrid() {
  return Array(ROWS).fill(null).map(() => Array(COLS).fill(null));
}

function randomType(max) {
  return Math.floor(Math.random() * max);
}

function findMatches(grid, typeCount) {
  const matched = new Set();
  const key = (r, c) => `${r},${c}`;
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      const cell = grid[r][c];
      if (cell == null) continue;
      let horz = 1;
      while (c + horz < COLS && grid[r][c + horz]?.type === cell.type) horz++;
      if (horz >= MIN_MATCH) for (let i = 0; i < horz; i++) matched.add(key(r, c + i));
      let vert = 1;
      while (r + vert < ROWS && grid[r + vert][c]?.type === cell.type) vert++;
      if (vert >= MIN_MATCH) for (let i = 0; i < vert; i++) matched.add(key(r + i, c));
    }
  }
  return matched;
}

function hasMatches(grid, typeCount) {
  return findMatches(grid, typeCount).size > 0;
}

function fillRandomNoMatches(typeCount) {
  const grid = createEmptyGrid();
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      let type;
      let attempts = 0;
      do {
        type = randomType(typeCount);
        grid[r][c] = { type, id: `${r}-${c}-${Date.now()}-${Math.random()}` };
        attempts++;
        if (attempts > 20) break;
      } while (attempts <= 20 && (horizontalMatchAt(grid, r, c) || verticalMatchAt(grid, r, c)));
      if (attempts > 20) grid[r][c] = { type: randomType(typeCount), id: `${r}-${c}-${Date.now()}-${Math.random()}` };
    }
  }
  return grid;
}

function horizontalMatchAt(grid, r, c) {
  const cell = grid[r][c];
  if (!cell) return false;
  let left = c;
  while (left > 0 && grid[r][left - 1]?.type === cell.type) left--;
  let right = c;
  while (right < COLS - 1 && grid[r][right + 1]?.type === cell.type) right++;
  return right - left + 1 >= MIN_MATCH;
}

function verticalMatchAt(grid, r, c) {
  const cell = grid[r][c];
  if (!cell) return false;
  let top = r;
  while (top > 0 && grid[top - 1][c]?.type === cell.type) top--;
  let bottom = r;
  while (bottom < ROWS - 1 && grid[bottom + 1][c]?.type === cell.type) bottom++;
  return bottom - top + 1 >= MIN_MATCH;
}

function dropAndRefill(grid, typeCount) {
  const next = createEmptyGrid();
  for (let c = 0; c < COLS; c++) {
    let write = ROWS - 1;
    for (let r = ROWS - 1; r >= 0; r--) {
      if (grid[r][c] != null) {
        const movedDown = write !== r;
        next[write][c] = {
          ...grid[r][c],
          id: `${write}-${c}-${Date.now()}-${Math.random()}`,
          dropIn: movedDown,
        };
        write--;
      }
    }
    for (let w = write; w >= 0; w--) {
      next[w][c] = {
        type: randomType(typeCount),
        id: `new-${w}-${c}-${Date.now()}-${Math.random()}`,
        dropIn: true,
      };
    }
  }
  return next;
}

function swapCells(grid, r1, c1, r2, c2) {
  const g = grid.map((row) => row.map((cell) => (cell ? { ...cell } : null)));
  const t = g[r1][c1];
  g[r1][c1] = g[r2][c2];
  g[r2][c2] = t;
  return g;
}

function isAdjacent(r1, c1, r2, c2) {
  return (Math.abs(r1 - r2) === 1 && c1 === c2) || (Math.abs(c1 - c2) === 1 && r1 === r2);
}

function CandyCrushGame({ updateHeader }) {
  const navigate = useNavigate();
  const S3_URL = process.env.REACT_APP_S3_PATH || "";
  const storedUserData = JSON.parse(sessionStorage.getItem("userData")) || {};
  const isDemoBypass =
    String(storedUserData?.sessionId).toLowerCase() === "demobypass" &&
    String(storedUserData?.organizationId).toLowerCase() === "demobypass";

  const [themeData, setThemeData] = useState(null);
  const [candyTypes, setCandyTypes] = useState([]);
  const [grid, setGrid] = useState(() => createEmptyGrid());
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(60);
  const [dragStart, setDragStart] = useState(null);
  const [dragPreview, setDragPreview] = useState(null);
  const [swapping, setSwapping] = useState(null);
  const [gameComplete, setGameComplete] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [sparkleCells, setSparkleCells] = useState(() => new Set());
  const scoreRef = useRef(0);
  scoreRef.current = score;
  const gridStateRef = useRef(grid);
  gridStateRef.current = grid;
  const demoFirstSwapDoneRef = useRef(false);
  const gridRef = useRef(null);

  useEffect(() => {
    const storedTheme = sessionStorage.getItem("themeData");
    if (!storedTheme) {
      navigate("/login");
      return;
    }
    const parsed = JSON.parse(storedTheme);
    setThemeData(parsed);
    setCandyTypes(getCandyTypes(parsed, S3_URL));
  }, [navigate, S3_URL]);

  useEffect(() => {
    if (!themeData) return;
    const timerStr = themeData.timer?.trim();
    if (timerStr) {
      const parts = timerStr.split(":").map(Number);
      const total = (parts[0] || 0) * 60 + (parts[1] || 0);
      setTimeLeft(total > 0 ? total : 60);
    } else {
      setTimeLeft(0);
    }
  }, [themeData]);

  useEffect(() => {
    if (themeData?.game_title?.trim()) {
      document.title = themeData.game_title.trim();
    }
  }, [themeData]);

  useEffect(() => {
    if (!themeData?.background_desk && !themeData?.background_mob) return;
    const body = document.body;
    const updateBackground = () => {
      if (!body.classList.contains("common-bg")) return;
      const isMobile = window.innerWidth <= 768;
      const isPortrait = window.matchMedia("(orientation: portrait)").matches;
      if (isMobile && isPortrait && themeData.background_mob) {
        body.style.backgroundImage = `url(${themeData.background_mob})`;
      } else if (themeData.background_desk) {
        body.style.backgroundImage = `url(${themeData.background_desk})`;
      }
    };
    updateBackground();
    window.addEventListener("resize", updateBackground);
    return () => window.removeEventListener("resize", updateBackground);
  }, [themeData]);

  useEffect(() => {
    if (candyTypes.length < 2) return;
    setGrid(fillRandomNoMatches(candyTypes.length));
  }, [candyTypes.length]);

  useEffect(() => {
    if (!storedUserData?.sessionId || isDemoBypass) return;

    const check = async () => {
      try {
        const res = await getScore({
          userId: storedUserData.userId || storedUserData.userid,
          sessionId: storedUserData.sessionId,
          organizationId: storedUserData.organizationId,
        });

        if (res?.status === "success") {
          const data = res.data || {};

          // Only redirect to thankyou when gameover is true from API (user already finished)
          const isGameOver =
            data.gameover === true ||
            data.gameover === "1" ||
            String(data.gameover).toLowerCase() === "true";

          if (isGameOver) {
            navigate("/thankyou", {
              replace: true,
              state: { points: data.total_score, time: data.time },
            });
          }
          // Otherwise let the user play the game
        }
      } catch (e) {
        console.error(e);
      }
    };

    check();
  }, [navigate]);

  useEffect(() => {
    if (timeLeft <= 0 || gameComplete) return;
    const id = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          if (!isDemoBypass) completeGame(0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [timeLeft, gameComplete]);

  const completeGame = useCallback(async (finalTimeSeconds) => {
    if (gameComplete) return;
    setGameComplete(true);
    const timeToUse = typeof finalTimeSeconds === "number" ? finalTimeSeconds : timeLeft;
    const min = Math.floor(timeToUse / 60);
    const sec = timeToUse % 60;
    const displayTime = `${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
    const timeForDb = (themeData?.timer?.trim()) || displayTime;
    // Delay so last-second match points finish updating scoreRef before we read and send to API
    const DELAY_MS = 1200;
    setTimeout(async () => {
      const pointsToSend = scoreRef.current;
      if (!isDemoBypass) {
        try {
          await updateScore({
            userId: storedUserData.userId || storedUserData.userid,
            sessionId: storedUserData.sessionId,
            organizationId: storedUserData.organizationId,
            points: pointsToSend,
            time: timeForDb,
          });
          await updategameover({
            userId: storedUserData.userId || storedUserData.userid,
            sessionId: storedUserData.sessionId,
            organizationId: storedUserData.organizationId,
            gamecomplete: true,
            gameover: true,
            time: timeForDb,
            points: pointsToSend,
          });
        } catch (err) {
          console.error(err);
        }
      }
      // cheer from both bottom corners, then go to the thank-you page
      const goThankYou = () => navigate("/thankyou", { state: { points: pointsToSend, time: timeForDb } });
      fireCornerConfetti().then(goThankYou, goThankYou);
    }, DELAY_MS);
  }, [gameComplete, timeLeft, isDemoBypass, navigate, themeData]);

  function formatTime() {
    const min = Math.floor(timeLeft / 60);
    const sec = timeLeft % 60;
    return `${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  }

  useEffect(() => {
    updateHeader?.({ backButtonRedirect: "/intro" });
  }, [updateHeader]);

  // Base points per match (3 in a row)
  const pointsPerMatch = Math.max(1, parseInt(themeData?.points, 10) || 10);
  // Bonus points per extra candy when 4+ in a row (from themeData.bonus_points)
  const bonusPoints = Math.max(0, parseInt(themeData?.bonus_points, 10) || 0);

  const performSwap = useCallback(
    (fromR, fromC, toR, toC) => {
      if (isDemoBypass && !demoFirstSwapDoneRef.current) {
        demoFirstSwapDoneRef.current = true;
        const pointsToSend = scoreRef.current;
        const min = Math.floor(timeLeft / 60);
        const sec = timeLeft % 60;
        const displayTime = `${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
        const timeForDb = (themeData?.timer?.trim()) || displayTime;
        updateScore({
          userId: storedUserData.userId || storedUserData.userid,
          sessionId: storedUserData.sessionId,
          organizationId: storedUserData.organizationId,
          points: pointsToSend,
          time: timeForDb,
        }).catch((err) => console.error(err));
        updategameover({
          userId: storedUserData.userId || storedUserData.userid,
          sessionId: storedUserData.sessionId,
          organizationId: storedUserData.organizationId,
          gamecomplete: true,
          gameover: true,
          time: timeForDb,
          points: pointsToSend,
        }).catch((err) => console.error(err));
        // Pass points and time with demo data in sessionStorage
        const updatedUserData = {
          ...storedUserData,
          points: pointsToSend,
          time: timeForDb,
          total_score: pointsToSend,
        };
        sessionStorage.setItem("userData", JSON.stringify(updatedUserData));
       


        setTimeout(() => {
          Swal.fire({
            icon: 'info',
            text: 'Thank you for playing. Subscribe to any PLAN to play with your peers.',
            confirmButtonText: 'OK',
            customClass: { popup: 'custom-success-popup' },
            allowOutsideClick: false,
            allowEscapeKey: false
          }).then(result => {
            if (result.isConfirmed) {
              window.location.href =
                process.env.REACT_APP_BASE_URL + "/plans";
            }
          });
        }, 500);
      }

      const currentGrid = gridStateRef.current;
      const from = { r: fromR, c: fromC };
      const to = { r: toR, c: toC };
      const afterSwap = swapCells(currentGrid, fromR, fromC, toR, toC);
      const match1 = findMatches(afterSwap, candyTypes.length).size > 0;
      setIsAnimating(true);
      setSwapping({ from, to });
      setTimeout(() => {
        setGrid(afterSwap);
        setSwapping(null);
        if (!match1) {
          setTimeout(() => {
            setSwapping({ from, to });
            setTimeout(() => {
              const revertedGrid = swapCells(afterSwap, fromR, fromC, toR, toC);
              const withoutDropIn = revertedGrid.map((row) =>
                row.map((cell) => (cell ? { ...cell, dropIn: false } : null))
              );
              setGrid(withoutDropIn);
              setSwapping(null);
              setIsAnimating(false);
            }, 280);
          }, 20);
          return;
        }
        let totalScore = scoreRef.current;
        const processMatches = (current) => {
          const matched = findMatches(current, candyTypes.length);
          if (matched.size === 0) {
            setGrid(current);
            setScore(totalScore);
            setIsAnimating(false);
            setSparkleCells(new Set());
            return;
          }
          const extraCandies = Math.max(0, matched.size - 3);
          const matchPoints = pointsPerMatch + extraCandies * bonusPoints;
          totalScore += matchPoints;
          scoreRef.current = totalScore; // keep ref in sync so API gets latest score (e.g. last-second points)
          setScore(totalScore);
          setSparkleCells(new Set(matched));
          setTimeout(() => {
            const cleared = current.map((row, ri) =>
              row.map((cell, ci) => (matched.has(`${ri},${ci}`) ? null : cell))
            );
            const filled = dropAndRefill(cleared, candyTypes.length);
            setGrid(filled);
            setSparkleCells(new Set());
            setTimeout(() => processMatches(filled), 400);
          }, 380);
        };
        setTimeout(() => processMatches(afterSwap), 50);
      }, 280);
    },
    [candyTypes.length, pointsPerMatch, bonusPoints, isDemoBypass, timeLeft, themeData, navigate, storedUserData]
  );

  const handlePointerDown = useCallback(
    (e, r, c) => {
      if (gameComplete || isAnimating || candyTypes.length < 2) return;
      const cell = grid[r][c];
      if (!cell) return;
      e.preventDefault();
      const clientX = e.clientX ?? e.touches?.[0]?.clientX;
      const clientY = e.clientY ?? e.touches?.[0]?.clientY;
      setDragStart({ r, c });
      const gridEl = gridRef.current;
      let x = clientX, y = clientY;
      if (gridEl) {
        const rect = gridEl.getBoundingClientRect();
        const INSET = 24;
        x = Math.max(rect.left + INSET, Math.min(rect.right - INSET, clientX));
        y = Math.max(rect.top + INSET, Math.min(rect.bottom - INSET, clientY));
      }
      setDragPreview({ x, y });
    },
    [gameComplete, isAnimating, candyTypes.length, grid]
  );

  useEffect(() => {
    if (!dragStart) return;
    const getClientCoords = (e) => {
      if (e.touches?.length) return { x: e.touches[0].clientX, y: e.touches[0].clientY };
      return { x: e.clientX, y: e.clientY };
    };
    const INSET = 24; // half drag-preview size so center stays inside grid
    const handleMove = (e) => {
      e.preventDefault();
      let { x, y } = getClientCoords(e);
      const gridEl = gridRef.current;
      if (gridEl) {
        const rect = gridEl.getBoundingClientRect();
        x = Math.max(rect.left + INSET, Math.min(rect.right - INSET, x));
        y = Math.max(rect.top + INSET, Math.min(rect.bottom - INSET, y));
      }
      setDragPreview({ x, y });
    };
    const handleUp = (e) => {
      let upCoords = e.changedTouches?.length
        ? { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY }
        : getClientCoords(e);
      let x = upCoords.x, y = upCoords.y;
      const gridEl = gridRef.current;
      if (gridEl) {
        const rect = gridEl.getBoundingClientRect();
        x = Math.max(rect.left + INSET, Math.min(rect.right - INSET, x));
        y = Math.max(rect.top + INSET, Math.min(rect.bottom - INSET, y));
      }
      const target = document.elementFromPoint(x, y)?.closest?.(".candy-cell");
      const from = dragStart;
      setDragStart(null);
      setDragPreview(null);
      if (!from || !target) return;
      const toR = parseInt(target.getAttribute("data-row"), 10);
      const toC = parseInt(target.getAttribute("data-col"), 10);
      if (isNaN(toR) || isNaN(toC)) return;
      if (from.r === toR && from.c === toC) return;
      if (!isAdjacent(from.r, from.c, toR, toC)) return;
      performSwap(from.r, from.c, toR, toC);
    };
    document.addEventListener("mousemove", handleMove, { passive: false });
    document.addEventListener("mouseup", handleUp);
    document.addEventListener("touchmove", handleMove, { passive: false });
    document.addEventListener("touchend", handleUp, { passive: false });
    document.addEventListener("touchcancel", handleUp, { passive: false });
    return () => {
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleUp);
      document.removeEventListener("touchmove", handleMove);
      document.removeEventListener("touchend", handleUp);
      document.removeEventListener("touchcancel", handleUp);
    };
  }, [dragStart, performSwap]);

  if (!themeData) return null;

  const typeCount = Math.max(2, candyTypes.length);

  return (
    <div className={`candy-game-container ${dragStart ? "dragging-active" : ""}`}>
      <div
        className="candy-top-bar"

      >
        <div className="candy-timer" aria-label="Time remaining" style={{
          backgroundColor: themeData?.button_color || "#5c2d91",
          color: themeData?.button_text_color || "#fff",
        }}>
          Time: {formatTime()}
        </div>
        <div className="candy-title" style={{
          backgroundColor: themeData?.button_color || "#5c2d91",
          color: themeData?.button_text_color || "#fff",
        }}>{themeData?.landing_page_title || "Candy Crush"}</div>
        <div className="candy-score" style={{
          backgroundColor: themeData?.button_color || "#5c2d91",
          color: themeData?.button_text_color || "#fff",
        }} aria-label="Score">
          Points: {score}
        </div>
      </div>

      <div className="candy-board-wrap">
        <div
          ref={gridRef}
          className="candy-grid"
          role="grid"
          aria-label="8 by 8 candy grid"
          style={{
            "--cols": COLS,
            ...(themeData?.candy_bg_color && { backgroundColor: themeData.candy_bg_color }),
          }}
        >
          {grid.map((row, r) =>
            row.map((cell, c) => {
              const isSwappingFrom = swapping?.from?.r === r && swapping?.from?.c === c;
              const isSwappingTo = swapping?.to?.r === r && swapping?.to?.c === c;
              const isDragging = dragStart?.r === r && dragStart?.c === c;
              const isDropIn = cell?.dropIn === true;
              const isSparkle = sparkleCells.has(`${r},${c}`);
              let slideStyle = {};
              if (swapping && (isSwappingFrom || isSwappingTo)) {
                const dc = (isSwappingFrom ? swapping.to.c - c : swapping.from.c - c);
                const dr = (isSwappingFrom ? swapping.to.r - r : swapping.from.r - r);
                slideStyle = {
                  transition: "transform 0.25s ease-out",
                  transform: `translate(${dc * 100}%, ${dr * 100}%)`,
                  zIndex: 2,
                };
              }
              return (
                <button
                  key={`${r}-${c}-${cell?.id ?? "empty"}`}
                  type="button"
                  className={`candy-cell ${isDragging ? "dragging" : ""} ${isSwappingFrom || isSwappingTo ? "swapping" : ""} ${isDropIn ? "candy-drop-in" : ""} ${isSparkle ? "match-sparkle" : ""}`}
                  data-row={r}
                  data-col={c}
                  onMouseDown={(e) => handlePointerDown(e, r, c)}
                  onTouchStart={(e) => handlePointerDown(e, r, c)}
                  style={{
                    backgroundColor: themeData?.candy_bg_color
                      ? `${themeData.candy_bg_color}33`
                      : themeData?.button_color
                        ? `${themeData.button_color}22`
                        : "rgba(92,45,145,0.1)",
                    ...slideStyle,
                  }}
                  aria-label={cell ? `Candy at row ${r + 1} column ${c + 1}` : "Empty"}
                  disabled={isAnimating || gameComplete}
                >
                  {cell && candyTypes[cell.type] ? (
                    <img src={candyTypes[cell.type]} alt="" className="candy-img" draggable="false" />
                  ) : null}
                </button>
              );
            })
          )}
        </div>
        {dragStart && dragPreview && grid[dragStart.r]?.[dragStart.c] && candyTypes[grid[dragStart.r][dragStart.c].type] && (
          <div
            className="candy-drag-preview"
            style={{
              left: dragPreview.x,
              top: dragPreview.y,
            }}
            aria-hidden
          >
            <img src={candyTypes[grid[dragStart.r][dragStart.c].type]} alt="" draggable="false" />
          </div>
        )}
      </div>

      {/* <p className="candy-hint">Drag a candy onto an adjacent one to swap and make 3+ in a row or column.</p> */}
    </div>
  );
}

export default CandyCrushGame;
